/* eslint-disable max-lines */
"use client";
import React, { useState, useEffect, useRef, useMemo } from "react";
import ImageZoom from "../../molecules/imageZoom";
import PropTypes from "prop-types";
import CustomImage from "../../../components/atoms/customImage";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import isEmpty from "lodash.isempty";
import Slider from "react-slick";
import { SlickNextArrow, SlickPrevArrow } from "../carouselHelpers";
import { PDP_SLIDER_OPTIONS } from "../../../constants/common/splideOptions";
import { setDisplayGalleryClickedImage } from "../../../store/slices/pdpSlice";
import { debounce, formatClassNames } from "../../../utils/common";
import VideoPlayer from "../../atoms/videoPlayer";
import { PRODUCT_VIDEO_POSITION, S3_IMAGE_DOMAIN } from "../../../constants/pageConstants/pdpConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import PDPKeyHeighLights from "../pdpKeyHeighLights";
import { EyeIcon } from "@phosphor-icons/react";

/**
 * Component that displays a product gallery with a carousel, image zoom, and optional video support.
 * @param {Object} props - The component props.
 * @param {string[]} props.productImages - Array of image URLs to be displayed in the carousel.
 * @param {string} props.productReviewsAndRatings - Product reviews and ratings information.
 * @param {string} props.productVideoLink - Optional video link associated with the product.
 * @param {string} props.urlIdentifier - Unique identifier used in the product URL.
 * @param {string} props.skuCode - SKU code for the product.
 * @param {boolean} props.isMobile - Flag indicating if the view is on a mobile device.
 * @param {Object} props.ctImageViewedData - Details of the CT image viewed data.
 * @returns {JSX.Element} The rendered product display gallery component.
 */
const ProductDisplayGallery = ({
  productImages,
  productVideoLink,
  urlIdentifier,
  isMobile,
  ctImageViewedData,
  disclaimerText,
  titleForShareLink,
}) => {
  const imageDomain = process.env.NEXT_PUBLIC_ASSET_PREFIX;
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const sliderRef = useRef(null);
  const videoRef = useRef(null);
  const [currentImageSelectedIndex, setCurrentImageSelectedIndex] = useState(0);
  const [playVideo, setPlayVideo] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const { keyHighlightDetails = {} } = useSelector(
    (state) => ({
      keyHighlightDetails: state?.pdp?.clientCurrentClickedItem?.keyHighlightDetails,
    }),
    shallowEqual,
  );

  const isYoutube = productVideoLink?.indexOf("youtube") > -1;
  const modifiedProductMedia = useMemo(() => {
    const updatedProductImages = productImages?.slice();
    !isEmpty(productVideoLink) && productImages?.length
      ? updatedProductImages?.splice(1, 0, productVideoLink)
      : updatedProductImages;
    return updatedProductImages;
  }, [productVideoLink, productImages]);

  useEffect(() => {
    setIsClient(true);
  }, [imageDomain, modifiedProductMedia]);

  /**
   * Highlights the current image in the carousel.
   * @param {string} item - The URL of the image.
   * @param {number} index - The index of the image in the list.
   * @returns {boolean} True if the image is the currently selected image, otherwise false.
   */
  const highlightCurrentImage = (index) => {
    if (isMobile) {
      return false;
    }

    return currentImageSelectedIndex == index;
  };

  /**
   * Handles the click event for an image to set it as the currently selected image.
   * Debounced to prevent rapid updates.
   *
   * @param {number} index - The index of the clicked image in the product images array.
   */
  const onImageClick = useMemo(
    () =>
      debounce((index) => {
        const dynamicIndexValue = !index ? currentImageSelectedIndex : index;
        const isPersonalized = modifiedProductMedia[dynamicIndexValue]?.includes(S3_IMAGE_DOMAIN);
        const eventData = {
          ...ctImageViewedData,
          image_position: dynamicIndexValue,
          is_personalized: isPersonalized,
          is_video: productVideoLink && dynamicIndexValue === PRODUCT_VIDEO_POSITION ? true : false,
        };
        trackEvent(cleverTapCommonEvents.pdpImageViewed, eventData, ["clevertap"]);
        const mediaSrc =
          index === PRODUCT_VIDEO_POSITION && productVideoLink
            ? modifiedProductMedia[dynamicIndexValue]
            : isPersonalized
              ? modifiedProductMedia[dynamicIndexValue]
              : imageDomain + modifiedProductMedia[dynamicIndexValue];

        dispatch(setDisplayGalleryClickedImage({ url: mediaSrc, index }));
      }, 100),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentImageSelectedIndex, productVideoLink, modifiedProductMedia, imageDomain, ctImageViewedData],
  );

  /**
   * Handles the click event for an image in the carousel to set it as the currently selected image.
   * @param {string} item - The URL of the image.
   * @param {number} index - The index of the image in the list.
   */
  const onSlideClick = (index) => {
    if (isMobile) {
      onImageClick(index);
    } else {
      const eventData = {
        ...ctImageViewedData,
        image_position: index,
        is_personalized: modifiedProductMedia[index]?.includes(S3_IMAGE_DOMAIN),
        is_video: productVideoLink && index === PRODUCT_VIDEO_POSITION ? true : false,
      };
      trackEvent(cleverTapCommonEvents.pdpImageViewed, eventData, ["clevertap"]);
      setCurrentImageSelectedIndex(index);
    }
    if (productVideoLink && index !== PRODUCT_VIDEO_POSITION) {
      setPlayVideo(false);
    }
  };

  /**
   *
   */
  const onPlayingHandler = (event, isSlideAutoPlay) => {
    event?.stopPropagation();
    if (videoRef?.current) {
      if (isSlideAutoPlay) {
        const video = videoRef.current;
        if (!video.getAttribute("src")) {
          const source = video.dataset.src || video.getAttribute("data-src");
          if (source) {
            video.setAttribute("src", source);
            video.load();
          }
        }
        setPlayVideo(true);
        video.controls = true;
        video.play();
        // sliderRef?.current?.slickPause();
      } else {
        setPlayVideo(false);
        videoRef.current.controls = false;
        videoRef.current?.pause();
        // sliderRef?.current?.slickPlay(); // Switch-off autoplay
      }
    }
  };

  /**
   * Renders a CustomImage component for displaying a play/pause icon.
   * @param {string} src - The source URL of the play/pause icon image.
   * @returns {JSX.Element} The CustomImage component with the specified source URL and styles.
   */
  const renderPlayPauseIcon = (src, width = 40, height = 40) => {
    return (
      <CustomImage
        src={src}
        alt={`${playVideo ? "Pause" : "Play"}`}
        width={width}
        height={height}
        sizes="10vw"
        isPriority={false}
        itemProp="image"
        className="m-auto"
      />
    );
  };

  /**
   * Renders an iframe element for displaying a video with autoplay and loop enabled.
   * @param {string} videoLink - The URL of the video to be embedded in the iframe.
   * @returns {JSX.Element} The iframe element with the specified video URL and parameters.
   */
  const renderProductVideo = (videoLink) => {
    return (
      <div className="relative w-full pb-[100%] pr-10">
        {isYoutube ? (
          <>
            {isMobile && <div className="absolute left-0 top-0 z-10 h-full w-full" />}
            <iframe
              border="0"
              src={`${videoLink}?autoplay=1&modestbranding=1&showinfo=0&rel=0&loop=1`}
              target="_parent"
              height="100%"
              name="Product Video"
              width="100%"
              title="iframe Product Video"
              className="absolute left-0 top-0 h-full w-full rounded-2xl bg-black-900"
            />
          </>
        ) : (
          <>
            {!playVideo && (
              <button
                className={formatClassNames(
                  "absolute z-9 flex h-full w-full justify-center bg-[#0000004d]",
                  !isMobile && "rounded-2xl",
                )}
                onClick={(e) => onPlayingHandler(e, true)}
              >
                {renderPlayPauseIcon("/icons/play.svg", 60, 60)}
              </button>
            )}
            <VideoPlayer
              customRef={videoRef}
              videoUrl={videoLink}
              title="Product Video"
              classes={formatClassNames("absolute left-1 top-0 h-full w-full bg-black-900", !isMobile && "rounded-2xl")}
              isMuted={true}
              onPlaying={onPlayingHandler}
              isPlaysInline={true}
              preload="none"
              poster={modifiedProductMedia?.[0]}
            />
          </>
        )}
      </div>
    );
  };

  /**
   * @param {string} item - consist url of image
   * @returns {JSX.Element} A JSX element containing CustomImage.
   */
  const renderImage = (item, index) => {
    const imageSrc =
      index !== PRODUCT_VIDEO_POSITION || !productVideoLink
        ? item?.includes(S3_IMAGE_DOMAIN)
          ? item
          : imageDomain + item
        : productImages?.[0]?.includes(S3_IMAGE_DOMAIN)
          ? productImages?.[0]
          : imageDomain + productImages?.[0];

    return (
      <>
        {imageSrc.includes(S3_IMAGE_DOMAIN) && (
          <div
            className={`absolute ${isMobile ? "right-12 top-12" : "right-4 top-4"} flex h-[24px] w-[24px] items-center justify-center rounded-full bg-white-900 bg-opacity-60`}
          >
            <EyeIcon size={16} color="#191A0B" />
          </div>
        )}
        <CustomImage
          src={isMobile ? imageSrc : imageSrc?.replace("/l/", "/s/")}
          width={500}
          height={500}
          alt={`${titleForShareLink} - ${index + 1}` || `${item}`}
          id={`thumbnail_${index + 1}`}
          borderRadius={isMobile ? 0 : 6.4}
          customStyle={{
            width: "100%",
            height: "fit-content",
          }}
          sizes="(max-width: 768px) 100vw, 50vw"
          itemProp="image"
          isPriority={false}
          isOptimize={false}
        />
        {disclaimerText && index === 0 && isMobile && (
          <div className="absolute bottom-0 left-0 flex w-full items-center gap-4 bg-[#fff6] px-12 py-8 backdrop-blur-[16px]">
            <p className="text-12 font-500 leading-[14px] text-grey-500 opacity-75 md:leading-16">{disclaimerText}</p>
          </div>
        )}
      </>
    );
  };

  /**
   * @param {string} item - The URL of the image to display.
   * @param {number} index - The index of the current image.
   * @returns {JSX.Element} A JSX element that conditionally renders the image, video, or additional elements based on the state.
   */
  const handleViewInMobile = (item, index) => {
    if (index === PRODUCT_VIDEO_POSITION && productVideoLink) {
      return renderProductVideo(productVideoLink);
    }
    return renderImage(item, index);
  };

  /**
   * Handles the click event for an image in the carousel.
   * @param {string} item - The URL of the image.
   * @param {number} index - The index of the image in the list.
   * @returns {JSX.Element} The rendered image div.
   */
  const renderSlideContent = (item, index) => {
    const isVideoSlide = index === PRODUCT_VIDEO_POSITION && productVideoLink;
    return (
      <div
        // eslint-disable-next-line react/jsx-handler-names
        onClick={!isVideoSlide || !isMobile || isYoutube ? () => onSlideClick(index) : null}
        key={index}
        className={`relative w-full cursor-pointer rounded-lg duration-300 ease-in-out hover:border-darkOliveGreen md:border-2 ${highlightCurrentImage(index) ? "border-darkOliveGreen" : ""}`}
      >
        {!isMobile ? (
          <div className="desktop hidden h-full w-full md:flex">
            <div className="relative h-full w-full">
              {productVideoLink && index == 1 && (
                <>
                  <div className="absolute h-full w-full rounded-[6.4px] bg-black-900 opacity-30" />
                  <div className="absolute flex h-full w-full justify-center">
                    {playVideo
                      ? renderPlayPauseIcon("/icons/drawer-close.svg")
                      : renderPlayPauseIcon("/icons/play.svg")}
                  </div>
                </>
              )}
              {renderImage(item, index)}
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-col">{handleViewInMobile(item, index)}</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex gap-16" id="productGallery">
      <meta itemProp="url" content={`/gift/${urlIdentifier}`} />
      <div className="relative w-full md:flex md:w-[15%] lg:w-[15%]">
        {isMobile && !isEmpty(keyHighlightDetails) && <PDPKeyHeighLights keyHighlightDetails={keyHighlightDetails} />}
        {modifiedProductMedia?.length === PRODUCT_VIDEO_POSITION && isClient ? (
          // Render single image without slider
          <>
            <CustomImage
              src={
                modifiedProductMedia[0]?.includes(S3_IMAGE_DOMAIN)
                  ? modifiedProductMedia[0]
                  : imageDomain + modifiedProductMedia[0]
              }
              width={500}
              height={500}
              alt={modifiedProductMedia[0]}
              itemProp="image"
              isPriority={true}
              className="h-fit w-[100%] rounded-[6.4px]"
              isOptimize={false}
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            {disclaimerText && isMobile && (
              <div className="absolute bottom-0 left-0 flex w-full items-center gap-4 bg-[#fff6] px-12 py-8 backdrop-blur-[16px]">
                <p className="text-12 font-500 leading-[14px] text-grey-500 opacity-75 md:leading-16">
                  {disclaimerText}
                </p>
              </div>
            )}
          </>
        ) : isClient && modifiedProductMedia?.length > 1 ? (
          // Render multiple images with Slick slider
          <Slider
            {...PDP_SLIDER_OPTIONS}
            ref={(slider) => (sliderRef.current = slider)}
            nextArrow={<SlickNextArrow />}
            prevArrow={<SlickPrevArrow />}
            className="pdpSlider"
            autoplaySpeed={3000}
            afterChange={(currentSlide) => {
              if (productVideoLink && currentSlide !== PRODUCT_VIDEO_POSITION) {
                onPlayingHandler(null, false);
              }
            }}
            dotsClass={formatClassNames("slick-dots", productVideoLink && "pdpPlayDots")}
          >
            {modifiedProductMedia.map((item, index) => renderSlideContent(item, index))}
          </Slider>
        ) : (
          // Render placeholder when no image exists
          <CustomImage
            src="/icons/placeholder.webp"
            width={500}
            height={500}
            alt="Placeholder Image"
            itemProp="image"
            isPriority={true}
            sizes="(max-width: 768px) 100vw, 50vw"
            className="h-fit w-[100%] rounded-[6.4px]"
          />
        )}
      </div>

      {!isMobile ? (
        <div className="hidden h-[100%] w-[100%] md:flex">
          <meta itemProp="url" content={`/gift/${urlIdentifier}`} />

          {currentImageSelectedIndex === PRODUCT_VIDEO_POSITION && productVideoLink ? (
            renderProductVideo(productVideoLink)
          ) : (
            <ImageZoom
              imageSrc={
                modifiedProductMedia?.[currentImageSelectedIndex]?.includes(S3_IMAGE_DOMAIN)
                  ? modifiedProductMedia?.[currentImageSelectedIndex]
                  : imageDomain + modifiedProductMedia?.[currentImageSelectedIndex]?.replace("/x/", "/l/")
              }
              onImageClick={() => onImageClick()}
              disclaimerText={disclaimerText}
              productTitle={titleForShareLink}
            />
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

ProductDisplayGallery.propTypes = {
  productImages: PropTypes.arrayOf(PropTypes.string).isRequired,
  productVideoLink: PropTypes.string,
  urlIdentifier: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  ctImageViewedData: PropTypes.object,
  disclaimerText: PropTypes.string,
  titleForShareLink: PropTypes.string,
};

export default ProductDisplayGallery;
